import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { Progress } from '../components/ui/Progress';
import { Spinner } from '../components/ui/Spinner';

type MgrBoot = {
  set: (value: number) => void;
  done: () => void;
};

declare global {
  interface Window {
    __mgrBoot?: MgrBoot;
  }
}

let pageBusy = 0;
const pageListeners = new Set<() => void>();

function emitPageBusy() {
  pageListeners.forEach((listener) => listener());
}

/** First-visit lazy routes report in so the boot overlay waits for the page chunk. */
export function useBootPagePending() {
  useEffect(() => {
    pageBusy += 1;
    emitPageBusy();
    return () => {
      pageBusy = Math.max(0, pageBusy - 1);
      emitPageBusy();
    };
  }, []);
}

function usePageBusy() {
  const [busy, setBusy] = useState(() => pageBusy > 0);
  useEffect(() => {
    const onChange = () => setBusy(pageBusy > 0);
    pageListeners.add(onChange);
    onChange();
    return () => {
      pageListeners.delete(onChange);
    };
  }, []);
  return busy;
}

function waitForFonts(): Promise<void> {
  if (!document.fonts?.ready) return Promise.resolve();
  return document.fonts.ready.then(() => undefined);
}

function waitForWindowLoad(): Promise<void> {
  if (document.readyState === 'complete') return Promise.resolve();
  return new Promise((resolve) => {
    window.addEventListener('load', () => resolve(), { once: true });
  });
}

function waitForMainImages(): Promise<void> {
  const main = document.getElementById('main');
  if (!main) return Promise.resolve();
  const pending = [...main.querySelectorAll('img')].filter((img) => !img.complete);
  if (pending.length === 0) return Promise.resolve();
  return Promise.all(
    pending.map(
      (img) =>
        new Promise<void>((resolve) => {
          img.addEventListener('load', () => resolve(), { once: true });
          img.addEventListener('error', () => resolve(), { once: true });
        }),
    ),
  ).then(() => undefined);
}

/**
 * Drives the HTML boot overlay (progress + spinner) until the current player
 * page chunk, fonts, and first-paint assets are in. In-app navigations use the
 * existing page Spinner only — the overlay is removed after first entry.
 */
export function BootGate({
  siteLoading,
  sessionRestoring,
}: {
  siteLoading: boolean;
  sessionRestoring: boolean;
}) {
  const { t } = useTranslation();
  const pageBusyNow = usePageBusy();
  const [fontsReady, setFontsReady] = useState(() => document.fonts?.status === 'loaded');
  const [windowLoaded, setWindowLoaded] = useState(() => document.readyState === 'complete');
  const [imagesReady, setImagesReady] = useState(false);
  const [minHoldElapsed, setMinHoldElapsed] = useState(false);
  // Suspense fallback mounts one tick after BootGate; wait so we do not
  // dismiss before the first lazy page reports in.
  const [awaitingFirstPage, setAwaitingFirstPage] = useState(true);

  useEffect(() => {
    let cancelled = false;
    void waitForFonts().then(() => {
      if (!cancelled) setFontsReady(true);
    });
    void waitForWindowLoad().then(() => {
      if (!cancelled) setWindowLoaded(true);
    });
    const hold = window.setTimeout(() => {
      if (!cancelled) setMinHoldElapsed(true);
    }, 240);
    const firstPage = window.setTimeout(() => {
      if (!cancelled) setAwaitingFirstPage(false);
    }, 80);
    return () => {
      cancelled = true;
      window.clearTimeout(hold);
      window.clearTimeout(firstPage);
    };
  }, []);

  const bootstrapReady = !siteLoading && !sessionRestoring;
  const pageReady = bootstrapReady && !pageBusyNow && !awaitingFirstPage;

  useEffect(() => {
    if (!pageReady) {
      setImagesReady(false);
      return;
    }
    let cancelled = false;
    const frame = requestAnimationFrame(() => {
      void waitForMainImages().then(() => {
        if (!cancelled) setImagesReady(true);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
    };
  }, [pageReady]);

  const progress = (() => {
    let value = 0.12;
    if (fontsReady) value = Math.max(value, 0.36);
    if (!siteLoading) value = Math.max(value, 0.55);
    if (!sessionRestoring) value = Math.max(value, 0.7);
    if (pageReady) value = Math.max(value, 0.86);
    if (windowLoaded) value = Math.max(value, 0.93);
    if (imagesReady) value = Math.max(value, 0.98);
    return value;
  })();

  useEffect(() => {
    window.__mgrBoot?.set(progress);
  }, [progress]);

  const ready = pageReady && fontsReady && windowLoaded && imagesReady && minHoldElapsed;

  useEffect(() => {
    if (!ready) return;
    const frame = requestAnimationFrame(() => window.__mgrBoot?.done());
    return () => cancelAnimationFrame(frame);
  }, [ready]);

  useEffect(() => {
    const failsafe = window.setTimeout(() => window.__mgrBoot?.done(), 10_000);
    return () => window.clearTimeout(failsafe);
  }, []);

  const slot = typeof document !== 'undefined' ? document.getElementById('boot-progress-slot') : null;
  const label = typeof document !== 'undefined' ? document.getElementById('boot-label') : null;

  useEffect(() => {
    document.getElementById('boot-progress-fallback')?.remove();
  }, []);

  useEffect(() => {
    if (label) label.textContent = t('common.loading');
  }, [label, t]);

  if (!slot) return null;

  return createPortal(
    <>
      <Spinner className="text-primary size-6" />
      <Progress value={progress} tone="primary" size="md" label={t('common.loading')} className="w-56" />
    </>,
    slot,
  );
}
